import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminPromotionAPI } from '../services/api';
import '../styles/PromotionView.css';

const PromotionView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [promotion, setPromotion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPromotion = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminPromotionAPI.getById(id);
      setPromotion(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching promotion:', err);
      setError(err.response?.data?.message || 'Failed to fetch promotion details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPromotion();
  }, [fetchPromotion]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="promotion-view-container">
        <div className="loading">Loading promotion details...</div>
      </div>
    );
  }

  if (error || !promotion) {
    return (
      <div className="promotion-view-container">
        <div className="error-state">
          <p>{error || 'Promotion not found'}</p>
          <button className="btn btn-primary" onClick={() => navigate('/promotions')}>
            Back to Promotions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="promotion-view-container">
      <div className="promotion-view-header">
        <button className="btn-back" onClick={() => navigate('/promotions')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Promotions
        </button>
        {/* <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={() => navigate(`/promotions/${id}/edit`)}
          >
            Edit Promotion
          </button>
        </div> */}
      </div>

      <div className="promotion-view-content">
        {/* Banner Section */}
        {promotion.bannerImage && (
          <div className="promotion-banner-section">
            <img
              src={promotion.bannerImage}
              alt={promotion.title}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}

        {/* Category Title and Image Section */}
        {(promotion.categoryTitle || promotion.categoryImageUrl) && (
          <div className="promotion-category-banner">
            {promotion.categoryImageUrl && (
              <div className="category-banner-image">
                <img
                  src={promotion.categoryImageUrl}
                  alt={promotion.categoryTitle || 'Category'}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
            {promotion.categoryTitle && (
              <div className="category-banner-title">
                <h2>{promotion.categoryTitle}</h2>
              </div>
            )}
          </div>
        )}

        {/* Title and Status */}
        <div className="promotion-title-section">
          <div>
            <h1>{promotion.title}</h1>
            {promotion.promoCode && (
              <div className="promo-code-display">
                <span className="promo-code-label">Promo Code:</span>
                <span className="promo-code-value">{promotion.promoCode}</span>
              </div>
            )}
          </div>
          <span className={`status-badge-large ${promotion.status?.toLowerCase()}`}>
            {promotion.status}
          </span>
        </div>

        {/* Date and Discount Info */}
        <div className="promotion-meta">
          <div className="meta-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <span>{formatDate(promotion.startDate)} - {formatDate(promotion.endDate)}</span>
          </div>

          {promotion.discountValue && (
            <div className="meta-item discount">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                <line x1="7" y1="7" x2="7.01" y2="7"></line>
              </svg>
              <span className="discount-value">
                {promotion.discountType === 'percentage'
                  ? `${promotion.discountValue}% OFF`
                  : promotion.discountType === 'fixed'
                  ? `$${promotion.discountValue} OFF`
                  : 'Buy One Get One'}
              </span>
            </div>
          )}

          {promotion.maxUsage && (
            <div className="meta-item">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              <span>Used {promotion.currentUsage || 0} / {promotion.maxUsage} times</span>
            </div>
          )}
        </div>

        {/* Description Section */}
        <div className="promotion-section">
          <h2>Description</h2>
          <p className="promotion-description">{promotion.description}</p>
        </div>

        {/* Terms & Conditions */}
        {promotion.termsAndConditions && promotion.termsAndConditions.length > 0 && (
          <div className="promotion-section">
            <h2>Terms & Conditions</h2>
            <ul className="terms-list">
              {promotion.termsAndConditions.map((term, index) => (
                <li key={index}>{term}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Applicable Categories */}
        {promotion.applicableCategories && promotion.applicableCategories.length > 0 && (
          <div className="promotion-section">
            <h2>Applicable Categories</h2>
            <div className="categories-grid">
              {promotion.applicableCategories.map(category => (
                <div key={category._id} className="category-card">
                  <div className="category-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                      <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                      <line x1="12" y1="22.08" x2="12" y2="12"></line>
                    </svg>
                  </div>
                  <span>{category.title}</span>
                  <button
                    className="view-category-btn"
                    onClick={() => navigate('/categories')}
                  >
                    View Category
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Featured Products */}
        {promotion.featuredProducts && promotion.featuredProducts.length > 0 && (
          <div className="promotion-section">
            <h2>Featured Products</h2>
            <div className="products-grid">
              {promotion.featuredProducts.map(product => (
                <div key={product._id} className="product-card-view">
                  {product.productImage && (
                    <div className="product-image-view">
                      <img
                        src={product.productImage}
                        alt={product.productName}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div class="no-image-view">No Image</div>';
                        }}
                      />
                    </div>
                  )}
                  <div className="product-info-view">
                    <h3>{product.productName}</h3>
                    <p className="product-catalog">{product.catalogNumber}</p>
                    {product.packSize && (
                      <p className="product-pack-size">{product.packSize}</p>
                    )}
                    {product.brandName && (
                      <span className="product-brand">{product.brandName}</span>
                    )}
                  </div>
                  <button
                    className="view-product-btn"
                    onClick={() => navigate(`/products/${product._id}`)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                    View Product
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Avail Now Button */}
        <div className="promotion-cta">
          {/* <button className="btn btn-cta">
            Avail Now
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
              <polyline points="15 3 21 3 21 9"></polyline>
              <line x1="10" y1="14" x2="21" y2="3"></line>
            </svg>
          </button> */}
        </div>
      </div>
    </div>
  );
};

export default PromotionView;
